import { useState } from 'react';

type Category = 'all' | 'coding' | 'system-design' | 'case-study' | 'behavioral' | 'resume';

interface InterviewCard {
  id: number;
  company: string;
  role: string;
  category: Category;
  duration: string;
  skills: string[];
  href?: string;
}

const cards: InterviewCard[] = [
  {
    id: 1,
    company: 'Amazon',
    role: 'SDE 1',
    category: 'coding',
    duration: '10 min',
    skills: ['Arrays', 'Dynamic Programming', 'Graphs'],
    href: 'https://preview.learn41.com/job/594',
  },
  {
    id: 2,
    company: 'Google',
    role: 'Software Engineer',
    category: 'system-design',
    duration: '10 min',
    skills: ['Distributed Systems', 'Scalability', 'APIs'],
    href: 'https://preview.learn41.com/job/597',
  },
  {
    id: 3,
    company: 'McKinsey',
    role: 'Business Analyst',
    category: 'case-study',
    duration: '10 min',
    skills: ['Market Sizing', 'Frameworks', 'Communication'],
    href: 'https://preview.learn41.com/job/599',
  },
  {
    id: 4,
    company: 'Infosys',
    role: 'Systems Engineer',
    category: 'behavioral',
    duration: '7 min',
    skills: ['Problem Solving', 'Teamwork', 'Communication'],
    href: 'https://preview.learn41.com/job/601',
  },
  {
    id: 5,
    company: 'Bosch',
    role: 'Embedded Engineer',
    category: 'coding',
    duration: '10 min',
    skills: ['Data Structures', 'C/C++', 'Algorithms'],
    href: 'https://preview.learn41.com/job/595',
  },
  {
    id: 6,
    company: 'Flipkart',
    role: 'Backend Engineer',
    category: 'system-design',
    duration: '10 min',
    skills: ['Microservices', 'Databases', 'Caching'],
    href: 'https://preview.learn41.com/job/598',
  },
  {
    id: 7,
    company: 'Deloitte',
    role: 'Consultant',
    category: 'case-study',
    duration: '10 min',
    skills: ['Profitability', 'Strategy', 'Quantitative Analysis'],
    href: 'https://preview.learn41.com/job/600',
  },
  {
    id: 8,
    company: 'Samsung',
    role: 'Software Developer',
    category: 'coding',
    duration: '7 min',
    skills: ['OOD', 'Data Structures', 'Concurrency'],
    href: 'https://preview.learn41.com/job/596',
  },
  {
    id: 9,
    company: 'Goldman Sachs',
    role: 'Technology Analyst',
    category: 'resume',
    duration: '7 min',
    skills: ['Experience Deep Dive', 'Technical Leadership', 'Impact'],
    href: 'https://preview.learn41.com/job/602',
  },
];

const filters: { label: string; value: Category }[] = [
  { label: 'All', value: 'all' },
  { label: 'Coding', value: 'coding' },
  { label: 'System Design', value: 'system-design' },
  { label: 'Case Study', value: 'case-study' },
  { label: 'Behavioral', value: 'behavioral' },
  { label: 'Resume Deep Dive', value: 'resume' },
];

export default function InteractiveDemo() {
  const [active, setActive] = useState<Category>('all');

  const filtered = active === 'all' ? cards : cards.filter((c) => c.category === active);

  return (
    <section id="try-interview" className="bg-background py-20 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="font-playfair text-3xl md:text-5xl font-medium italic text-foreground text-center mb-4">
          Pick an interview to try
        </h2>
        <p className="text-center text-muted-foreground mb-2 max-w-xl mx-auto">
          Choose a company and format. Sign in and start, it only takes a few minutes.
        </p>
        <p className="text-center text-sm text-primary-accent-600 font-medium mb-10">
          No credit card needed
        </p>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setActive(f.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-muted-foreground hover:border-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((card) => (
            <div
              key={card.id}
              className="bg-card rounded-xl p-6 border border-border flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-accent-600">
                  {card.company}-style
                </span>
                <span className="text-xs text-muted-foreground capitalize">
                  {card.category.replace('-', ' ')}
                </span>
              </div>

              <h3 className="text-lg font-semibold text-foreground mb-2">
                {card.role}
              </h3>

              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <span>{card.duration}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {card.skills.map((skill) => (
                  <span
                    key={skill}
                    className="text-xs bg-background text-muted-foreground px-2.5 py-1 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="mt-auto">
                {card.href ? (
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center bg-primary text-primary-foreground font-semibold py-2.5 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Start Interview
                  </a>
                ) : (
                  <button className="w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-lg hover:bg-primary-600 transition-colors">
                    Start Interview
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
